/** biome-ignore-all lint/suspicious/noArrayIndexKey: the order or the content of questions is readonly */
import {
  Button,
  Checkbox,
  Group,
  Paper,
  Radio,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import type React from "react";
import { useState } from "react";
import type { QuestionDefinition } from "@/api-types";
import type { SurveyDefinition } from "@/utils/survey";
import type { SurveyValue } from "@/utils/survey-schema";

export interface SurveyFormProps {
  survey: SurveyDefinition;
  initialValues?: Record<string, SurveyValue>;
  onSubmit: (values: Record<string, SurveyValue>, isDraft: boolean) => void;
  loading?: boolean;
  readOnly?: boolean;
}

type Value = string | boolean | string[];

export const SurveyForm: React.FC<SurveyFormProps> = ({
  survey,
  initialValues = {},
  onSubmit,
  loading = false,
  readOnly = false,
}) => {
  const [values, setValues] = useState<Record<string, Value>>(() => {
    const initial: Record<string, Value> = {};
    if (initialValues) {
      Object.entries(initialValues).forEach(([key, value]) => {
        if (
          typeof value === "string" ||
          typeof value === "boolean" ||
          Array.isArray(value)
        ) {
          initial[key] = value as Value;
        }
      });
    }
    return initial;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateValue = (questionId: string, value: Value) => {
    if (readOnly) return;

    setValues((prev) => ({
      ...prev,
      [questionId]: value,
    }));

    // Clear error when user makes a change
    if (errors[questionId]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };

  const handleSubmit = (isDraft: boolean) => {
    if (readOnly) return;

    // Validate the form
    const newErrors: Record<string, string> = {};

    if (!isDraft) {
      survey.questions.forEach((question) => {
        const value = values[question.id];
        const noValue = !value || value === "";
        const isEmptyArray =
          value && Array.isArray(value) && value.length === 0;
        if (question.required && (noValue || isEmptyArray)) {
          newErrors[question.id] = "This question is required";
        }
      });
    }

    // If there are validation errors, show them
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Submit the form
    onSubmit(values, isDraft);
  };

  const renderQuestion = (question: QuestionDefinition) => {
    const { id, label, description, type, required, options = [] } = question;
    const value = values[id];
    const error = errors[id];

    let inputComponent: React.ReactNode;

    switch (type) {
      case "SHORT_TEXT":
        inputComponent = (
          <TextInput
            value={(value as string) || ""}
            onChange={(e) => updateValue(id, e.target.value)}
            disabled={readOnly}
            placeholder={readOnly && !value ? "Not answered" : "Your answer"}
            error={error}
          />
        );
        break;

      case "LONG_TEXT":
        inputComponent = (
          <Textarea
            value={(value as string) || ""}
            onChange={(e) => updateValue(id, e.target.value)}
            disabled={readOnly}
            minRows={3}
            placeholder={readOnly && !value ? "Not answered" : "Your answer"}
            error={error}
          />
        );
        break;

      case "YES_NO":
        inputComponent = (
          <Radio.Group
            value={value !== undefined ? value.toString() : ""}
            onChange={(val) => updateValue(id, val === "true")}
            error={error}
          >
            <Group mt="xs">
              <Radio value="true" label="Yes" disabled={readOnly} />
              <Radio value="false" label="No" disabled={readOnly} />
            </Group>
          </Radio.Group>
        );
        break;

      case "MULTIPLE_CHOICE":
        inputComponent = (
          <Radio.Group
            value={(value as string) || ""}
            onChange={(val) => updateValue(id, val)}
            error={error}
          >
            <Stack mt="xs">
              {options?.map((option, index) => (
                <Radio
                  key={index}
                  value={option}
                  label={option}
                  disabled={readOnly}
                />
              ))}
            </Stack>
          </Radio.Group>
        );
        break;

      case "MULTIPLE_CHOICE_MULTI": {
        const selectedValues = (value as string[]) || [];
        inputComponent = (
          <Stack mt="xs">
            {options?.map((option, index) => (
              <Checkbox
                key={index}
                label={option}
                checked={selectedValues.includes(option)}
                onChange={(event) => {
                  const checked = event.currentTarget.checked;
                  const newSelectedValues = [...selectedValues];

                  if (checked && !newSelectedValues.includes(option)) {
                    newSelectedValues.push(option);
                  } else if (!checked && newSelectedValues.includes(option)) {
                    const idx = newSelectedValues.indexOf(option);
                    newSelectedValues.splice(idx, 1);
                  }

                  updateValue(id, newSelectedValues);
                }}
                disabled={readOnly}
              />
            ))}
            {error && (
              <Text color="red" size="sm">
                {error}
              </Text>
            )}
          </Stack>
        );
        break;
      }

      default:
        inputComponent = <Text color="red">Unsupported question type</Text>;
    }

    return (
      <Paper withBorder p="md" radius="md" key={id}>
        <Stack>
          <Group>
            <Text fw={500}>
              {label}
              {required && !readOnly && (
                <Text span color="red">
                  {" "}
                  *
                </Text>
              )}
            </Text>
          </Group>

          {description && (
            <Text size="sm" c="dimmed">
              {description}
            </Text>
          )}

          {inputComponent}
        </Stack>
      </Paper>
    );
  };

  return (
    <Stack>
      {survey.questions.map(renderQuestion)}

      {!readOnly && (
        <Group mt="md">
          <Button
            variant="outline"
            onClick={() => handleSubmit(true)}
            loading={loading}
            disabled={readOnly}
          >
            Save Draft
          </Button>
          <Button
            onClick={() => handleSubmit(false)}
            loading={loading}
            disabled={readOnly}
          >
            Submit
          </Button>
        </Group>
      )}
    </Stack>
  );
};
